import * as RadixPopover from '@radix-ui/react-popover'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { PropsWithChildren, useEffect, useState } from 'react'

import XIcon from '../../icons/x'
import Typography from './typography'

interface PopoverProps {
  /**
   * Title of the popover
   */
  title?: string

  /**
   * Trigger component that will open the popover
   */
  triggerComponent: React.ReactNode | ((open: boolean) => React.ReactNode)

  /**
   * Position of the popover relative to the trigger component
   * @default "center"
   */
  position?: 'start' | 'center' | 'end'

  /**
   * Side of the popover relative to the trigger component
   * @default "bottom"
   */
  side?: 'top' | 'bottom' | 'left' | 'right'

  /**
   * Offset of the popover relative to the trigger component
   * @default 0
   */
  alignOffset?: number

  /**
   * Whether the popover is open or not
   * @default false
   */
  isOpen?: boolean

  /**
   * Function that will be called when the popover is opened or closed
   * @param open - Whether the popover is open or not
   */
  onOpenChange?: (open: boolean) => void

  /**
   * Additional class name
   */
  className?: string
}

const Popover: React.FC<PropsWithChildren<PopoverProps>> = ({
  title,
  triggerComponent,
  position = 'center',
  side = 'bottom',
  alignOffset,
  isOpen = false,
  onOpenChange,
  className,
  children
}) => {
  const [open, setOpen] = useState(isOpen)

  useEffect(() => {
    setOpen(isOpen)
  }, [isOpen])

  return (
    <RadixPopover.Root
      open={open}
      onOpenChange={() => {
        setOpen(!open)
        if (onOpenChange) onOpenChange(!open)
      }}
    >
      <RadixPopover.Trigger asChild>
        {typeof triggerComponent === 'function' ? triggerComponent(open) : triggerComponent}
      </RadixPopover.Trigger>
      <RadixPopover.Portal>
        <RadixPopover.Content
          align={position}
          side={side}
          className={cn('rounded min-w-60 bg-white shadow z-30', className)}
          sideOffset={5}
          alignOffset={alignOffset}
        >
          {title && (
            <div className="flex items-center justify-between p-4 border-b border-white-active">
              <Typography variant="tag">{title}</Typography>
              <RadixPopover.Close className="rounded-full cursor-pointer" aria-label="Close">
                <XIcon color={tailwindColors.grey.light.active} height="12" width="12" />
              </RadixPopover.Close>
            </div>
          )}
          {children}
        </RadixPopover.Content>
      </RadixPopover.Portal>
    </RadixPopover.Root>
  )
}

export default Popover
